// TUT-450 — o comercial: propostas e versões, resposta pública, contratos, ordens de serviço, tarefas operacionais e trabalhos especiais.
import { demoApi } from "../demo-data";
import type {
  SpecialWork,
  SpecialWorkListResponse,
  CreateSpecialWorkInput,
  UpdateSpecialWorkInput,
  Proposal,
  ProposalSummary,
  ProposalListResponse,
  ProposalRequesterLink,
  CreateProposalRequesterLinkInput,
  SaveProposalInput,
  ProposalVersion,
  CommercialContractSummary,
  ServiceOrderSummary,
  OperationalTaskPlanInput,
  OperationalTask,
  PublicProposalResponseContext,
  PublicProposalDecisionInput,
  ProposalDecisionResult,
  RespondProposalInput,
} from "../types";
import { comFatia, request, requestBlob } from "./infra";
import type { FatiaPedida, ProposalListQuery } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDoComercial = {
  // Trabalhos especiais e propostas (TUT-040).
  specialWorks(
    fatia: FatiaPedida = {},
    search = "",
  ): Promise<SpecialWorkListResponse> {
    if (useDemoApi) return demoApi.specialWorks(fatia, search);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    return request<SpecialWorkListResponse>(
      `/special-works${comFatia(params, fatia)}`,
    );
  },
  proposals(fatia: ProposalListQuery = {}): Promise<ProposalListResponse> {
    if (useDemoApi) return demoApi.proposals(fatia);
    const params = new URLSearchParams();
    if (fatia.search) params.set("search", fatia.search);
    for (const campo of ["createdFrom", "createdTo", "sentFrom", "sentTo"] as const) {
      const dia = fatia[campo];
      if (dia) params.set(campo, dia);
    }
    // TUT-584 — a chave se repete ("ou" dentro do campo); a API chama de `modality` e `clientId`.
    for (const valor of fatia.status ?? []) params.append("status", valor);
    for (const valor of fatia.modalidade ?? []) params.append("modality", valor);
    for (const valor of fatia.cliente ?? []) params.append("clientId", valor);
    return request<ProposalListResponse>(
      `/proposals${comFatia(params, fatia)}`,
    );
  },
  proposal(id: string): Promise<Proposal> {
    if (useDemoApi) return demoApi.proposal(id);
    return request<Proposal>(`/proposals/${encodeURIComponent(id)}`);
  },
  proposalRequesterLinks(
    organizationId?: string,
  ): Promise<{ items: ProposalRequesterLink[] }> {
    if (useDemoApi) return demoApi.proposalRequesterLinks(organizationId);
    const params = new URLSearchParams();
    if (organizationId) params.set("organizationId", organizationId);
    const query = params.size ? `?${params}` : "";
    return request<{ items: ProposalRequesterLink[] }>(
      `/proposals/requester-links${query}`,
    );
  },
  createProposalRequesterLink(
    input: CreateProposalRequesterLinkInput,
  ): Promise<ProposalRequesterLink> {
    if (useDemoApi) return demoApi.createProposalRequesterLink(input);
    return request<ProposalRequesterLink>("/proposals/requester-links", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  createProposal(input: SaveProposalInput): Promise<Proposal> {
    if (useDemoApi) return demoApi.createProposal(input);
    return request<Proposal>("/proposals", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateProposal(id: string, input: SaveProposalInput): Promise<Proposal> {
    if (useDemoApi) return demoApi.updateProposal(id, input);
    return request<Proposal>(`/proposals/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  previewProposal(id: string): Promise<Blob> {
    if (useDemoApi) return demoApi.previewProposal(id);
    return requestBlob(`/proposals/${encodeURIComponent(id)}/preview`, {
      method: "POST",
    });
  },
  createProposalVersion(id: string): Promise<ProposalVersion> {
    if (useDemoApi) return demoApi.createProposalVersion(id);
    return request<ProposalVersion>(
      `/proposals/${encodeURIComponent(id)}/versions`,
      { method: "POST" },
    );
  },
  proposalVersions(id: string): Promise<{ items: ProposalVersion[] }> {
    if (useDemoApi) return demoApi.proposalVersions(id);
    return request<{ items: ProposalVersion[] }>(
      `/proposals/${encodeURIComponent(id)}/versions`,
    );
  },
  async downloadProposalVersion(id: string, versionId: string): Promise<string> {
    if (useDemoApi) return demoApi.downloadProposalVersion(id, versionId);
    const result = await request<{ url: string }>(
      `/proposals/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/download`,
    );
    return result.url;
  },
  sendProposal(id: string, versionId: string): Promise<ProposalSummary> {
    if (useDemoApi) return demoApi.sendProposal(id, versionId);
    return request<ProposalSummary>(`/proposals/${encodeURIComponent(id)}/send`, {
      method: "POST",
      body: JSON.stringify({ versionId }),
    });
  },
  respondProposal(
    id: string,
    input: RespondProposalInput,
  ): Promise<ProposalDecisionResult> {
    if (useDemoApi) return demoApi.respondProposal(id, input);
    return request<ProposalDecisionResult>(
      `/proposals/${encodeURIComponent(id)}/respond`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  publicProposalResponse(token: string): Promise<PublicProposalResponseContext> {
    if (useDemoApi) return demoApi.publicProposalResponse(token);
    return request<PublicProposalResponseContext>(
      `/public/proposals/${encodeURIComponent(token)}/response`,
    );
  },
  respondPublicProposal(
    token: string,
    input: PublicProposalDecisionInput,
  ): Promise<ProposalDecisionResult> {
    if (useDemoApi) return demoApi.respondPublicProposal(token, input);
    return request<ProposalDecisionResult>(
      `/public/proposals/${encodeURIComponent(token)}/response`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  contracts(): Promise<{ items: CommercialContractSummary[] }> {
    if (useDemoApi) return demoApi.contracts();
    return request<{ items: CommercialContractSummary[] }>("/contracts");
  },
  createContractServiceOrder(
    contractId: string,
    processId: string,
    tasks: OperationalTaskPlanInput[],
  ): Promise<ServiceOrderSummary & { created: boolean }> {
    if (useDemoApi)
      return demoApi.createContractServiceOrder(contractId, processId, tasks);
    return request<ServiceOrderSummary & { created: boolean }>(
      `/contracts/${encodeURIComponent(contractId)}/service-orders`,
      {
        method: "POST",
        body: JSON.stringify({ processId, tasks }),
      },
    );
  },
  createReexpertise(
    serviceOrderId: string,
    tasks: OperationalTaskPlanInput[],
  ): Promise<ServiceOrderSummary & { created: boolean }> {
    if (useDemoApi) return demoApi.createReexpertise(serviceOrderId, tasks);
    return request<ServiceOrderSummary & { created: boolean }>(
      `/service-orders/${encodeURIComponent(serviceOrderId)}/reexpertise`,
      { method: "POST", body: JSON.stringify({ tasks }) },
    );
  },
  async downloadServiceOrder(id: string): Promise<string> {
    if (useDemoApi) return demoApi.downloadServiceOrder(id);
    const result = await request<{ url: string }>(
      `/service-orders/${encodeURIComponent(id)}/download`,
    );
    return result.url;
  },
  operationalTasks(): Promise<{ items: OperationalTask[] }> {
    if (useDemoApi) return demoApi.operationalTasks();
    return request<{ items: OperationalTask[] }>("/operational-tasks");
  },
  transitionOperationalTask(
    id: string,
    to: "AWAITING_FILING",
  ): Promise<OperationalTask> {
    if (useDemoApi) return demoApi.transitionOperationalTask(id, to);
    return request<OperationalTask>(
      `/operational-tasks/${encodeURIComponent(id)}/transition`,
      { method: "PATCH", body: JSON.stringify({ to }) },
    );
  },
  createSpecialWork(input: CreateSpecialWorkInput): Promise<SpecialWork> {
    if (useDemoApi) return demoApi.createSpecialWork(input);
    return request<SpecialWork>("/special-works", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** TUT-473 (Q075) — edita o rascunho; depois de enviado, a API recusa. */
  updateSpecialWork(
    id: string,
    input: UpdateSpecialWorkInput,
  ): Promise<SpecialWork> {
    if (useDemoApi) return demoApi.updateSpecialWork(id, input);
    return request<SpecialWork>(`/special-works/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  transitionSpecialWork(
    id: string,
    input: { to: SpecialWork["status"]; reason?: string },
  ): Promise<SpecialWork> {
    if (useDemoApi) return demoApi.transitionSpecialWork(id, input);
    return request<SpecialWork>(
      `/special-works/${encodeURIComponent(id)}/transition`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
};
